/**
 * Public-facing accessibility components.
 *
 *   <AccessibilityIcons />   — compact icon strip for cards (≤ 4 confirmed-yes signals)
 *   <AccessibilityPanel />   — expandable full breakdown for attraction-detail pages
 *
 * Both follow the same hard rule: a feature is only displayed if the value is
 * exactly `true`. `false` and `null` are treated identically (i.e. "we won't
 * claim this exists"). That single rule is the difference between trustworthy
 * accessibility info and the kind of vague PR that wastes disabled visitors'
 * trips. See /app/backend/models.py::AccessibilityInfo for the schema.
 */
import React, { useState } from 'react';
import { Accessibility, Ear, Dog, ChevronDown, ExternalLink, Info, ShieldCheck, AlertTriangle, HelpCircle } from 'lucide-react';

const ALL_FIELDS = [
  // Mobility
  { key: 'step_free_entrance', label: 'Step-free entrance', icon: Accessibility, group: 'mobility' },
  { key: 'step_free_between_floors', label: 'Step-free between floors', icon: Accessibility, group: 'mobility' },
  { key: 'accessible_parking', label: 'Accessible parking', icon: Accessibility, group: 'mobility' },
  { key: 'accessible_toilet', label: 'Accessible toilet', icon: Accessibility, group: 'mobility' },
  { key: 'changing_places_toilet', label: 'Changing Places toilet', icon: ShieldCheck, group: 'mobility', highlight: true },
  { key: 'wheelchair_loan', label: 'Wheelchair to borrow', icon: Accessibility, group: 'mobility' },
  // Sensory & service
  { key: 'hearing_loop', label: 'Hearing loop', icon: Ear, group: 'sensory' },
  { key: 'large_print_available', label: 'Large-print available', icon: Info, group: 'sensory' },
  { key: 'sensory_friendly_sessions', label: 'Sensory-friendly sessions', icon: Info, group: 'sensory' },
  { key: 'assistance_dogs_welcome', label: 'Assistance dogs welcome', icon: Dog, group: 'sensory' },
  { key: 'carer_concession', label: 'Carer concession', icon: Info, group: 'sensory' },
];

// Provenance copy — keep these short, neutral and honest about what they mean.
// Trust is the whole point of the accessibility data; visitors with disabilities
// have been let down before by glib "we're accessible" statements.
const SOURCE_META = {
  venue: {
    short: 'Confirmed by the venue',
    long: 'The venue itself sent us these answers. Usually the most up-to-date, but only as good as their honesty — provision can drift between confirmations.',
  },
  osm: {
    short: 'OpenStreetMap — community-tagged',
    long: 'Pulled from OpenStreetMap\u2019s wheelchair / toilet tags, which volunteers update from on-the-ground visits. Generally trustworthy but rarely as thorough as a dedicated audit.',
  },
  changing_places: {
    short: 'Changing Places registry',
    long: 'From the official Changing Places facilities registry (run by the Changing Places Consortium). Only listed once a facility meets the full Changing Places spec.',
  },
  google_places: {
    short: 'Google Places',
    long: 'Pulled from Google\u2019s Places API. Often reflects what visitors leave in reviews rather than what staff confirm.',
  },
  mixed: {
    short: 'Multiple sources combined',
    long: 'Pieces came from a mix of the venue, OpenStreetMap and the Changing Places registry. Each individual field could come from any of those — we surface the most-recent confirmation.',
  },
};

function SourceBadge({ source }) {
  const [open, setOpen] = useState(false);
  const meta = SOURCE_META[source];
  if (!meta) return null;
  return (
    <span className="relative inline-flex items-center gap-1" data-testid="acc-source-badge">
      <span>{meta.short}</span>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="inline-flex w-4 h-4 items-center justify-center rounded-full text-charcoal/45 hover:text-charcoal focus:text-charcoal focus:outline-none"
        aria-label={`What does ${meta.short} mean?`}
        aria-expanded={open}
        data-testid="acc-source-help"
      >
        <HelpCircle size={11} />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-0 top-full mt-1 z-20 w-72 max-w-[calc(100vw-2rem)] bg-charcoal text-white text-xs leading-relaxed rounded-lg p-3 shadow-lg"
          data-testid="acc-source-tooltip"
        >
          {meta.long}
        </span>
      )}
    </span>
  );
}

// What we surface on cards — keep tight (≤4). Order = priority.
const CARD_PRIORITY = [
  'step_free_entrance',
  'changing_places_toilet',
  'hearing_loop',
  'assistance_dogs_welcome',
];

export function AccessibilityIcons({ accessibility, max = 4 }) {
  const a = accessibility || {};
  const present = CARD_PRIORITY.filter((k) => a[k] === true).slice(0, max);
  if (present.length === 0) return null;
  return (
    <div
      className="inline-flex items-center gap-1.5"
      role="list"
      aria-label="Accessibility features"
      data-testid="acc-icon-strip"
    >
      {present.map((k) => {
        const meta = ALL_FIELDS.find((f) => f.key === k);
        const Icon = meta.icon;
        return (
          <span
            key={k}
            role="listitem"
            title={meta.label}
            aria-label={meta.label}
            className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
              meta.highlight ? 'bg-mustard/25 text-charcoal' : 'bg-forest/15 text-forest'
            }`}
            data-testid={`acc-icon-${k}`}
          >
            <Icon size={12} strokeWidth={2.5} />
          </span>
        );
      })}
    </div>
  );
}

function hasAnyData(a) {
  if (!a) return false;
  return ALL_FIELDS.some((f) => a[f.key] === true || a[f.key] === false) ||
         a.notes || a.accessable_url || a.euans_guide_url || a.own_access_guide_url;
}

export function AccessibilityPanel({ accessibility, venueName }) {
  const a = accessibility || {};
  const [open, setOpen] = useState(false);
  if (!hasAnyData(a)) return null;

  const renderField = (f) => {
    const v = a[f.key];
    const stateLabel = v === true ? 'Yes' : v === false ? 'No' : 'Not confirmed';
    const stateClass =
      v === true ? 'text-forest' : v === false ? 'text-charcoal/65' : 'text-charcoal/40';
    return (
      <div
        key={f.key}
        className="flex items-center justify-between py-2 border-b border-charcoal/5 last:border-b-0"
        data-testid={`acc-panel-${f.key}`}
      >
        <div className="text-sm text-charcoal/85">{f.label}</div>
        <div className={`text-xs font-semibold ${stateClass}`}>{stateLabel}</div>
      </div>
    );
  };

  const verifiedDate = a.last_verified_at
    ? new Date(a.last_verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.target.open)}
      className="bg-stone border border-charcoal/10 rounded-3xl overflow-hidden"
      data-testid="accessibility-panel"
    >
      <summary className="flex items-center justify-between gap-3 px-5 py-4 cursor-pointer list-none hover:bg-stone/80">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-full bg-forest/15 text-forest flex items-center justify-center shrink-0">
            <Accessibility size={18} />
          </span>
          <div className="min-w-0">
            <div className="font-display text-lg leading-tight">Accessibility</div>
            <div className="text-xs text-charcoal/60 truncate flex items-center gap-1.5 flex-wrap">
              {verifiedDate
                ? <>
                    <span>Last confirmed {verifiedDate}</span>
                    {a.source && <span aria-hidden="true">·</span>}
                    {a.source && <SourceBadge source={a.source} />}
                  </>
                : 'Information not yet confirmed by the venue'}
            </div>
          </div>
        </div>
        <ChevronDown size={18} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </summary>

      <div className="px-5 pb-5 pt-2">
        {/* Trust message — first thing visitors see when they expand the panel.
            Sets honest expectations: we surface everything we've got, the data
            comes from a mix of trusted sources, and people with specific needs
            should still double-check before they travel. */}
        <div className="mt-3 bg-mustard/15 border border-mustard/40 rounded-2xl p-4 text-sm leading-relaxed text-charcoal/85" data-testid="acc-panel-trust-message">
          We&apos;re working with our partners to constantly improve the access
          information we hold. Everything we&apos;re able to share is shown here,
          including links to third-party guides where available. Please always
          double-check anything critical for your specific needs with the
          venue directly before you travel.
        </div>

        {/* Mobility */}
        <div className="mt-3 bg-white rounded-2xl p-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-heritage mb-2">Mobility</div>
          {ALL_FIELDS.filter((f) => f.group === 'mobility').map(renderField)}
        </div>
        {/* Sensory & service */}
        <div className="mt-3 bg-white rounded-2xl p-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-heritage mb-2">Sensory &amp; service</div>
          {ALL_FIELDS.filter((f) => f.group === 'sensory').map(renderField)}
        </div>

        {a.notes && (
          <div className="mt-3 bg-white rounded-2xl p-4 text-sm text-charcoal/85 leading-relaxed" data-testid="acc-panel-notes">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-heritage mb-2">Notes from the venue</div>
            {a.notes}
          </div>
        )}

        {(a.accessable_url || a.euans_guide_url || a.own_access_guide_url) && (
          <div className="mt-3 bg-white rounded-2xl p-4">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-heritage mb-2">Detailed access guides</div>
            <div className="flex flex-col gap-2">
              {a.accessable_url && (
                <a
                  href={a.accessable_link_auto ? 'https://www.accessable.co.uk/' : a.accessable_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-between text-sm text-heritage hover:text-charcoal font-semibold"
                  data-testid="acc-panel-accessable"
                >
                  {a.accessable_link_auto
                    ? 'See if the venue features on AccessAble'
                    : 'Detailed access guide on AccessAble'}{' '}
                  <ExternalLink size={12} />
                </a>
              )}
              {a.accessable_link_auto && (
                <div className="text-[11px] text-charcoal/55 mt-1" data-testid="acc-panel-accessable-note">
                  Opens AccessAble&apos;s website — AccessAble doesn&apos;t have
                  a guide for every Leicestershire venue, so use their search to check.
                </div>
              )}
              {a.euans_guide_url && (
                <a
                  href={a.euans_guide_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-between text-sm text-heritage hover:text-charcoal font-semibold"
                  data-testid="acc-panel-euans"
                >
                  Visitor reviews on Euan&apos;s Guide <ExternalLink size={12} />
                </a>
              )}
              {a.own_access_guide_url && (
                <a
                  href={a.own_access_guide_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-between text-sm text-heritage hover:text-charcoal font-semibold"
                  data-testid="acc-panel-own"
                >
                  {venueName ? `${venueName}'s own access guide` : "Venue's own access guide"} <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>
        )}

        <div className="mt-4 px-1 text-xs text-charcoal/55 leading-relaxed">
          Spot something wrong or out of date?{' '}
          <a
            href={`mailto:accessibility@goleicestershire.com?subject=Accessibility%20update%20for%20${encodeURIComponent(venueName || 'venue')}`}
            className="text-heritage hover:underline"
            data-testid="acc-panel-report"
          >
            Tell us
          </a>{' '}
          and we&apos;ll fix it.
        </div>
      </div>
    </details>
  );
}

/**
 * Reusable advisory shown alongside any accessibility-filtered surface
 * (explore filter results, Plan-My-Day with step-free, /accessible-days-out
 * editorial page). Two short, factual paragraphs — what to expect, and how to
 * report inaccuracies. `inline` variant is a tighter single-line version for
 * places where vertical space matters.
 */
export function AccessibilityDisclaimer({ variant = 'block' }) {
  if (variant === 'inline') {
    return (
      <div
        className="bg-mustard/15 border border-mustard/50 rounded-xl px-4 py-2.5 text-xs text-charcoal/80 flex items-start gap-2"
        data-testid="accessibility-disclaimer-inline"
        role="note"
      >
        <AlertTriangle size={14} className="text-charcoal/75 shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          We show what venues have confirmed. Provision can change — please
          double-check critical details with the venue before you travel.{' '}
          <a href="/accessibility" className="text-heritage hover:underline font-semibold">Read more</a>
        </div>
      </div>
    );
  }
  return (
    <div
      className="bg-mustard/15 border border-mustard/50 rounded-2xl p-4 md:p-5 text-sm text-charcoal/85 leading-relaxed"
      data-testid="accessibility-disclaimer"
      role="note"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="text-charcoal/80 shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <strong className="text-charcoal">A note on what we show.</strong>{' '}
          Accessibility information here comes from venues themselves and from
          open public-data sources (OpenStreetMap, Changing Places registry).
          We tag every claim with where it came from and the date it was last
          confirmed. <strong className="text-charcoal">Always double-check
          critical details with the venue before you travel</strong> — a lift
          can go out of service, a quiet hour can move. Spot something wrong?{' '}
          <a
            href="mailto:accessibility@goleicestershire.com?subject=Accessibility%20inaccuracy"
            className="text-heritage hover:underline font-semibold"
            data-testid="accessibility-disclaimer-report"
          >
            Tell us
          </a>{' '}
          and we&apos;ll fix it.
        </div>
      </div>
    </div>
  );
}
